import { ColumnDef } from '@tanstack/react-table';
import { Trash2 } from 'lucide-react';
import { DataTableColumnHeader } from '@/components/core';
import { Button } from '@/components/ui-kit/button';
import { Progress } from '@/components/ui-kit/progress';
import { formatDateOnly, formatOrgDateTime } from '@/lib/utils/custom-date/custom-date';
import { SurveyBatch } from '../../types/survey.types';

interface CreateBatchTableColumnsProps {
  t: (key: string, options?: Record<string, unknown>) => string;
  onView: (batch: SurveyBatch) => void;
  onDelete: (batch: SurveyBatch) => void;
}

/** Completed recipients as a whole percent of all recipients (0 without recipients). */
export const completionPercent = (
  batch: Pick<SurveyBatch, 'CompletedCount' | 'TotalRecipients'>
) =>
  batch.TotalRecipients > 0 ? Math.round((batch.CompletedCount / batch.TotalRecipients) * 100) : 0;

/**
 * All surveys (HR Module Prototype `PAGES["all-surveys"]`): Survey title, Sent date, Deadline, Progress
 * ("N of M" and a percent over a bar), View and Delete.
 */
export const createBatchTableColumns = ({
  t,
  onView,
  onDelete,
}: CreateBatchTableColumnsProps): ColumnDef<SurveyBatch>[] => [
  {
    id: 'Title',
    meta: { filter: { type: 'text' } },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('SURVEY_TITLE')} />,
    accessorFn: (row) => row.Title,
    cell: ({ getValue }) => <span className="font-medium">{getValue<string>()}</span>,
  },
  {
    id: 'SentDateUtc',
    meta: { filter: { type: 'datetime' } },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('SENT_DATE')} />,
    accessorFn: (row) => row.SentDateUtc,
    cell: ({ row }) =>
      formatOrgDateTime(row.original.SentDateUtc, row.original.Timezone || undefined) || '-',
  },
  {
    id: 'Deadline',
    meta: { filter: { type: 'date' } },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('DEADLINE')} />,
    accessorFn: (row) => row.Deadline,
    cell: ({ row }) => formatDateOnly(row.original.Deadline) || '-',
  },
  {
    id: 'Progress',
    // Completed out of total recipients; not one field the server can sort on.
    enableSorting: false,
    meta: { numeric: false },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('PROGRESS')} />,
    accessorFn: (row) => completionPercent(row),
    cell: ({ row }) => {
      const percent = completionPercent(row.original);
      return (
        <div className="grid min-w-36 gap-1">
          <div className="flex justify-between gap-2 text-xs tabular-nums">
            <span>
              {t('N_OF_M', {
                done: row.original.CompletedCount,
                total: row.original.TotalRecipients,
              })}
            </span>
            <span className="text-muted-foreground">{percent}%</span>
          </div>
          <Progress value={percent} aria-label={`${percent}%`} />
        </div>
      );
    },
  },
  {
    id: 'view',
    meta: { mobile: 'hidden' as const },
    header: () => <span className="text-xs font-medium">{t('VIEW')}</span>,
    enableSorting: false,
    enableHiding: false,
    cell: ({ row }) => (
      <Button
        variant="outline"
        size="sm"
        onClick={(event) => {
          event.stopPropagation();
          onView(row.original);
        }}
      >
        {t('VIEW')}
      </Button>
    ),
  },
  {
    id: 'actions',
    // Card: top right, as the old portal's card header.
    meta: { mobile: 'badge' as const },
    header: () => <span className="text-xs font-medium">{t('DELETE')}</span>,
    enableSorting: false,
    enableHiding: false,
    cell: ({ row }) => (
      <Button
        variant="ghost"
        size="icon-sm"
        onClick={(event) => {
          event.stopPropagation();
          onDelete(row.original);
        }}
        aria-label={t('DELETE_SURVEY_NAMED', { title: row.original.Title })}
        title={t('DELETE')}
      >
        <Trash2 className="size-4" />
      </Button>
    ),
  },
];
